import { useLocation } from 'react-router-dom';

import { searchConvertFiltersByIdToFiltersByName } from 'modules/search/helpers/searchConvertFiltersByIdToFiltersByName';
import { SearchPageLocationState } from 'modules/search/pages/Search/types/SearchPageLocationState';
import { searchGetRoutePathSwitch } from 'modules/search/routing/helpers/searchGetRoutePathSwitch';
import { useMainSelector } from 'store/hooks/useMainSelector';

import { SearchContextHeaderLocationState } from './SearchContextHeaderLocationState';

type Args = {
  currentObjectId: string;
};

export function useSearchContextHeaderInput({ currentObjectId }: Args) {
  const location = useLocation<SearchContextHeaderLocationState>();

  const {
    filters,
    pageIndex,
    query,
    resultsByPage,
    sort,
    isSearching,
    searchLocation,
  } = useMainSelector((state) => state.mainSearch);
  const searchRadius = useMainSelector((state) => state.searchRadius);

  const searchContext = location.state?.showSearchContext;
  if (!searchContext) return { searchContext: undefined, isSearching };

  const results = resultsByPage[pageIndex];
  if (!results) {
    return { searchContext: undefined, isSearching };
  }

  const currentResultIndex = results.hits.findIndex(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (hit: any) => hit.objectID === currentObjectId,
  );

  if (currentResultIndex === -1) return {};

  let previousResult = null;
  let previousResultPageIndex = null;

  if (currentResultIndex > 0) {
    previousResult = resultsByPage[pageIndex].hits[currentResultIndex - 1];
    previousResultPageIndex = pageIndex;
  } else if (pageIndex > 0) {
    const previousPage = resultsByPage[pageIndex - 1];

    if (previousPage) {
      previousResult = previousPage.hits[(previousPage.hitsPerPage || 0) - 1];

      if (previousResult) {
        previousResultPageIndex = pageIndex - 1;
      }
    }
  }

  let nextResult = null;
  let nextResultPageIndex = null;

  if (currentResultIndex < (results.hitsPerPage || 0) - 1) {
    nextResult = results.hits[currentResultIndex + 1];
    nextResultPageIndex = results.page;
  } else if (pageIndex < results.nbPages - 1) {
    const nextPage = resultsByPage[pageIndex + 1];

    if (nextPage) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line prefer-destructuring
      nextResult = nextPage.hits[0];
      nextResultPageIndex = pageIndex + 1;
    }
  }

  const searchResultsUrl = searchGetRoutePathSwitch({
    lang: CURRENT_LOCALE,
    filtersByName: searchConvertFiltersByIdToFiltersByName(filters),
    q: query,
    pageIndex,
    sort,
    radius: searchRadius.value,
  });

  const searchResultsState = {
    scrollToId: currentObjectId,
    searchLocation,
  } satisfies SearchPageLocationState;

  return {
    searchContextInput: {
      currentResultIndex,
      pageIndex,
      resultsByPage,
      searchResultsUrl,
      searchResultsState,
      previousResult,
      previousResultPageIndex,
      nextResult,
      nextResultPageIndex,
    },
    isSearching,
    searchFacets: searchContext.searchFacets,
  };
}

export type SearchContextInput = NonNullable<
  ReturnType<typeof useSearchContextHeaderInput>['searchContextInput']
>;
